import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Select,
  Tabs,
  TabsItem,
  Textarea,
} from "@/components";
import { axiosInstance, type ApiResponse } from "@/helpers/axios";
import type { Account } from "@/app/accounts/_types/accounts.types";
import type { Group } from "@/app/groups/_types/groups.types";
import { useDeviceEventsQuery } from "../../_hooks/useDeviceEventsQuery";
import { useDeviceQuery } from "../../_hooks/useDeviceQuery";
import { useUpdateDeviceMutation } from "../../_hooks/useUpdateDeviceMutation";
import type { ApiDevice } from "../../_types/devices.types";
import { formatDeviceTimestamp, formatDeviceTypeLabel } from "../../_utils/formatDevice";
import {
  accountAssignmentLabel,
  accountAssignmentValue,
  uniqueSortedStrings,
} from "../create-device-drawer/create-device-drawer.helpers";
import {
  DEVICE_DETAIL_TAB_LABELS,
  NONE_VALUE,
  buildPlacementPatch,
  ensureOption,
  hasLegacyMdmAssignments,
  type DeviceDetailTab,
} from "./device-detail-drawer.helpers";
import styles from "./DeviceDetailDrawer.module.scss";

type DeviceDetailDrawerProps = {
  deviceId: string | null;
  isOpen: boolean;
  onClose: () => void;
};

type PlacementOption = { value: string; label: string };

export const DeviceDetailDrawer = ({ deviceId, isOpen, onClose }: DeviceDetailDrawerProps) => {
  const [tab, setTab] = useState<DeviceDetailTab>("overview");
  const { data: device, isLoading, isError } = useDeviceQuery(deviceId);
  const eventsQuery = useDeviceEventsQuery(deviceId);
  const updateDevice = useUpdateDeviceMutation();

  const [location, setLocation] = useState(NONE_VALUE);
  const [organization, setOrganization] = useState(NONE_VALUE);
  const [assignedTo, setAssignedTo] = useState(NONE_VALUE);
  const [notes, setNotes] = useState("");
  const [placementError, setPlacementError] = useState<string | null>(null);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState<PlacementOption[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<PlacementOption[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<PlacementOption[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setTab("overview");
  }, [isOpen, deviceId]);

  useEffect(() => {
    if (!device) return;
    setLocation(device.location?.trim() || NONE_VALUE);
    setOrganization(device.organization?.trim() || NONE_VALUE);
    setAssignedTo(device.assignedTo?.trim() || NONE_VALUE);
    setNotes(device.notes ?? "");
    setPlacementError(null);
  }, [device?.id, device?.location, device?.organization, device?.assignedTo, device?.notes]);

  useEffect(() => {
    if (!isOpen || !device) return;

    let cancelled = false;
    const load = async () => {
      setPlacementLoading(true);
      try {
        const [devicesRes, groupsRes, accountsRes] = await Promise.all([
          axiosInstance.get<ApiResponse<ApiDevice[]>>("/v2/devices", {
            params: {
              page: 1,
              limit: 100,
              deviceType: "all",
              deviceStatus: "all",
              enrollment: "all",
              isActive: "1",
            },
          }),
          axiosInstance.get<ApiResponse<{ items: Group[] }>>("/v2/groups", {
            params: { kind: "all", archived: "0" },
          }),
          axiosInstance.get<ApiResponse<Account[]>>("/v2/accounts/customers", {
            params: { page: 1, limit: 100, role: "all", subscription: "all", period: "all" },
          }),
        ]);
        if (cancelled) return;

        const devices = devicesRes.data.data ?? [];
        const locations = uniqueSortedStrings(devices.map((d) => d.location)).map((value) => ({
          value,
          label: value,
        }));
        setLocationOptions(ensureOption(locations, device.location));

        const groups = groupsRes.data.data?.items ?? [];
        const orgs = groups
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((g) => ({ value: g.name, label: `${g.name} (${g.kind})` }));
        setOrganizationOptions(ensureOption(orgs, device.organization));

        const accounts = accountsRes.data.data ?? [];
        const assignees = accounts
          .slice()
          .sort((a, b) => a.email.localeCompare(b.email))
          .map((a) => ({
            value: accountAssignmentValue(a),
            label: accountAssignmentLabel(a),
          }));
        setAssigneeOptions(ensureOption(assignees, device.assignedTo));
      } catch {
        if (!cancelled) {
          setLocationOptions(ensureOption([], device.location));
          setOrganizationOptions(ensureOption([], device.organization));
          setAssigneeOptions(ensureOption([], device.assignedTo));
        }
      } finally {
        if (!cancelled) setPlacementLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, device?.id]);

  const allEvents = eventsQuery.data?.pages.flatMap((p) => p.items) ?? [];

  const title = isLoading ? "Loading…" : device?.name ?? "Device";
  const subtitle = useMemo(() => {
    if (!device) return undefined;
    return `${formatDeviceTypeLabel(device.deviceType)} · ${device.liveStatus === "online" ? "Live" : "Offline"}`;
  }, [device]);

  const savePlacement = async () => {
    if (!device) return;
    setPlacementError(null);
    try {
      await updateDevice.mutateAsync({
        id: device.id,
        patch: buildPlacementPatch({
          location,
          organization,
          assignedTo,
          notes,
        }),
      });
    } catch {
      setPlacementError("Could not save placement.");
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} ariaLabel="Device details">
      <DrawerHeader title={title} subtitle={subtitle} onClose={onClose} />
      <DrawerBody>
        <div className={styles["comp"]}>
          {isError ? <p className={styles["comp__error"]}>Could not load device.</p> : null}

          {device ? (
            <>
              <div className={styles["comp__tabs-wrap"]}>
                <Tabs
                  value={tab}
                  onChange={(v) => setTab(v as DeviceDetailTab)}
                  variant="secondary"
                  size="medium"
                >
                  {(Object.keys(DEVICE_DETAIL_TAB_LABELS) as DeviceDetailTab[]).map((key) => (
                    <TabsItem key={key} value={key}>
                      {DEVICE_DETAIL_TAB_LABELS[key]}
                    </TabsItem>
                  ))}
                </Tabs>
              </div>

              {tab === "overview" ? (
                <div className={styles["comp__panel"]} role="tabpanel">
                  <div className={styles["comp__intro"]}>
                    <h3 className={styles["comp__section-title"]}>Overview</h3>
                    <p className={styles["comp__subtitle"]}>
                      Enrollment, connectivity, and software versions for this device.
                    </p>
                  </div>
                  <div className={styles["comp__panel-content"]}>
                    <div className={styles["comp__meta-grid"]}>
                      <div>
                        <div className={styles["comp__label"]}>Status</div>
                        <div className={styles["comp__value"]}>{device.deviceStatus}</div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Live</div>
                        <div className={styles["comp__value"]}>{device.liveStatus}</div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Enrollment</div>
                        <div className={styles["comp__value"]}>
                          {device.enrolledAt ? formatDeviceTimestamp(device.enrolledAt) : "Pending"}
                        </div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Last seen</div>
                        <div className={styles["comp__value"]}>
                          {formatDeviceTimestamp(device.lastSeenAt ?? device.lastHeartbeat ?? undefined)}
                        </div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>App version</div>
                        <div className={styles["comp__value"]}>{device.appVersion ?? "—"}</div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Agent version</div>
                        <div className={styles["comp__value"]}>{device.agentVersion ?? "—"}</div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Target app</div>
                        <div className={styles["comp__value"]}>
                          {device.targetAppVersion ?? "Latest (channel)"}
                        </div>
                      </div>
                      <div>
                        <div className={styles["comp__label"]}>Update channel</div>
                        <div className={styles["comp__value"]}>{device.updateChannel ?? "stable"}</div>
                      </div>
                    </div>

                    {hasLegacyMdmAssignments(device) ? (
                      <section
                        className={styles["comp__legacy"]}
                        aria-label="Legacy MDM assignments"
                      >
                        <div className={styles["comp__intro"]}>
                          <h3 className={styles["comp__section-title"]}>
                            Legacy MDM assignments
                          </h3>
                          <p className={styles["comp__subtitle"]}>
                            Read-only IDs already stored on this device. Prototype Profiles and
                            Branding cannot change these values; placement saves omit these fields
                            so the server keeps them.
                          </p>
                        </div>
                        <div className={styles["comp__meta-grid"]}>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div className={styles["comp__label"]}>Device profile ID</div>
                            <div
                              className={styles["comp__value"]}
                              data-testid="legacy-device-profile-id"
                            >
                              {device.deviceProfileId?.trim() || "—"}
                            </div>
                          </div>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div className={styles["comp__label"]}>Device branding ID</div>
                            <div
                              className={styles["comp__value"]}
                              data-testid="legacy-device-branding-id"
                            >
                              {device.deviceBrandingId?.trim() || "—"}
                            </div>
                          </div>
                        </div>
                      </section>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {tab === "placement" ? (
                <div className={styles["comp__panel"]} role="tabpanel">
                  <div className={styles["comp__intro"]}>
                    <h3 className={styles["comp__section-title"]}>Placement</h3>
                    <p className={styles["comp__subtitle"]}>
                      Location is optional. Organization stores a plain string (group names are
                      suggestions only, not membership). Assignee uses account display names.
                      Prototype profiles and branding cannot be assigned here.
                    </p>
                  </div>
                  <div className={styles["comp__panel-content"]}>
                    {placementLoading ? (
                      <p className={styles["comp__hint"]}>Loading placement options…</p>
                    ) : null}
                    <div className={styles["comp__fields"]}>
                      <Select
                        id="detail-device-location"
                        label="Location"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={location}
                        disabled={updateDevice.isPending || placementLoading}
                        onChange={(v) => {
                          setLocation(String(v));
                          setPlacementError(null);
                        }}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {locationOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        id="detail-device-organization"
                        label="Organization"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={organization}
                        disabled={updateDevice.isPending || placementLoading}
                        onChange={(v) => {
                          setOrganization(String(v));
                          setPlacementError(null);
                        }}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {organizationOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        id="detail-device-assigned-to"
                        label="Assigned to"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={assignedTo}
                        disabled={updateDevice.isPending || placementLoading}
                        onChange={(v) => {
                          setAssignedTo(String(v));
                          setPlacementError(null);
                        }}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {assigneeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Textarea
                        id="detail-device-notes"
                        label="Description / notes"
                        value={notes}
                        disabled={updateDevice.isPending}
                        rows={4}
                        onChange={(v) => {
                          setNotes(v);
                          setPlacementError(null);
                        }}
                      />
                      <div className={styles["comp__actions"]}>
                        <Button
                          type="button"
                          size="small"
                          variant="secondary"
                          disabled={updateDevice.isPending}
                          onClick={() => void savePlacement()}
                        >
                          {updateDevice.isPending ? "Saving…" : "Save placement"}
                        </Button>
                      </div>
                      {placementError ? (
                        <span className={styles["comp__error"]}>{placementError}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === "history" ? (
                <div className={styles["comp__panel"]} role="tabpanel">
                  <div className={styles["comp__intro"]}>
                    <h3 className={styles["comp__section-title"]}>History</h3>
                    <p className={styles["comp__subtitle"]}>
                      Recent device events from the enrollment and MDM timeline.
                    </p>
                  </div>
                  <div className={styles["comp__panel-content"]}>
                    <div className={styles["comp__history"]}>
                      {eventsQuery.isLoading ? <p className={styles["comp__hint"]}>Loading history…</p> : null}
                      {eventsQuery.isError ? (
                        <p className={styles["comp__error"]}>Could not load history.</p>
                      ) : null}
                      {allEvents.map((ev) => (
                        <div key={ev.id} className={styles["comp__event"]}>
                          <div className={styles["comp__event-type"]}>{ev.eventType}</div>
                          <div className={styles["comp__event-time"]}>
                            {formatDeviceTimestamp(ev.occurredAt)}
                          </div>
                          {ev.actor?.firstName || ev.actor?.lastName ? (
                            <div className={styles["comp__event-time"]}>
                              {[ev.actor?.firstName, ev.actor?.lastName].filter(Boolean).join(" ")} (
                              {ev.actorType})
                            </div>
                          ) : (
                            <div className={styles["comp__event-time"]}>{ev.actorType}</div>
                          )}
                          {Object.keys(ev.payload).length > 0 ? (
                            <pre className={styles["comp__event-payload"]}>
                              {JSON.stringify(ev.payload, null, 2)}
                            </pre>
                          ) : null}
                        </div>
                      ))}
                      {eventsQuery.hasNextPage ? (
                        <div className={styles["comp__load-more"]}>
                          <Button
                            type="button"
                            variant="secondary"
                            size="small"
                            onClick={() => eventsQuery.fetchNextPage()}
                            disabled={eventsQuery.isFetchingNextPage}
                          >
                            {eventsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
                          </Button>
                        </div>
                      ) : null}
                      {!eventsQuery.isLoading &&
                      !eventsQuery.isError &&
                      allEvents.length === 0 ? (
                        <p className={styles["comp__hint"]}>No events yet.</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </DrawerBody>
    </Drawer>
  );
};
